import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const ProjectSwitcher = React.memo(({ currentProject, currentOrganizationId, onProjectSwitch }) => {
  const [availableProjects, setAvailableProjects] = useState([]);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const projectDropdownRef = useRef(null);
  const navigate = useNavigate();


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        projectDropdownRef.current &&
        !projectDropdownRef.current.contains(event.target)
      ) {
        setIsProjectDropdownOpen(false);
      }
    };

    if (isProjectDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProjectDropdownOpen]);

  // Fetch available projects for the dropdown
  useEffect(() => {
    const fetchProjects = async () => {
      if (!currentOrganizationId) return;
      
      try {
        setIsLoading(true);
        const apiService = (await import('../../../utils/apiService')).default;
        
        // The backend already returns projects that the user has access to based on their role
        const allProjects = await apiService.projects.getAll(currentOrganizationId);
        
        console.log('ProjectSwitcher: Loaded projects for dropdown:', allProjects?.length || 0, allProjects);
        
        // Simply use all projects returned by the API - the backend handles role-based filtering
        setAvailableProjects(Array.isArray(allProjects) ? allProjects : []);
      } catch (error) {
        console.error('Failed to load projects for switcher:', error);
        setAvailableProjects([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchProjects();
  }, [currentOrganizationId]);

  const handleSwitch = (project) => {
    if (project.id === currentProject?.id) {
      setIsProjectDropdownOpen(false);
      return;
    }
    
    setIsProjectDropdownOpen(false);
    if (onProjectSwitch) {
      onProjectSwitch(project);
    } else {
        // Default behavior if no handler provided
         navigate(`/kanban-board?id=${project.id}`, { 
          state: { project: project },
          replace: true
        });
    }
  };

  return (
    <div className="relative" ref={projectDropdownRef}>
      <button 
        onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
        className="group flex items-center gap-3 focus:outline-none p-1 -ml-1 rounded-md transition-all duration-200 active:scale-95 hover:bg-gray-100 dark:hover:bg-slate-800"
        aria-expanded={isProjectDropdownOpen}
        aria-haspopup="true"
        title="Switch Project"
      >
        <h1 className='text-3xl font-bold text-gray-900 dark:text-slate-100 group-hover:text-primary transition-colors'>
          {currentProject?.name || 'Project Board'}
        </h1>
        <Icon 
          name="ChevronDown" 
          size={24} 
          className={`text-gray-400 group-hover:text-primary transition-transform duration-200 mt-1 ${isProjectDropdownOpen ? 'rotate-180 text-primary' : ''}`} 
        />
      </button>
      
      {/* Project Switcher Dropdown */}
      {isProjectDropdownOpen && (
        <div className="absolute top-full left-0 mt-3 w-80 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl z-[1000] overflow-hidden ring-1 ring-black ring-opacity-5 animate-in fade-in zoom-in-95 duration-100">
            {isLoading ? (
             <div className="p-4 flex justify-center items-center">
                <Icon name="Loader2" size={24} className="animate-spin text-blue-500" />
             </div>
            ) : availableProjects.length > 0 ? (
            <div className="max-h-80 overflow-y-auto py-1 custom-scrollbar">
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider bg-gray-50 dark:bg-slate-900 flex justify-between items-center sticky top-0 backdrop-blur-sm z-10">
                <span>Switch Project</span>
                <span className="text-xs bg-gray-200 px-1.5 py-0.5 rounded text-gray-600">{availableProjects.length}</span>
              </div>
              {availableProjects.map(project => (
                <button
                  key={project.id}
                  onClick={() => handleSwitch(project)}
                  className={`w-full text-left px-4 py-3 flex items-start space-x-3 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors border-l-4 ${currentProject?.id === project.id ? 'bg-primary/10 dark:bg-primary/20 border-primary' : 'border-transparent'}`}
                >
                  <div className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${project.status === 'active' ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'}`}></div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium text-sm truncate ${currentProject?.id === project.id ? 'text-primary dark:text-primary-light' : 'text-gray-900 dark:text-slate-100'}`}>
                      {project.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-slate-400 truncate mt-0.5">
                      {project.description || 'No description'}
                    </div>
                  </div>
                  {currentProject?.id === project.id && (
                    <Icon name="Check" size={16} className="text-primary flex-shrink-0 mt-1" />
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Icon name="FolderOpen" size={24} className="mx-auto text-gray-300 mb-2" />
              <div className="text-sm text-gray-500">No other projects found</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default ProjectSwitcher;
